import { useEffect } from "react";
import TexarkanaHeroSection from "./components/HeroSection";
import TexarkanaStickyNav from "./components/StickyNav";
import TexarkanaProcessingTimeSection from "./components/ProcessingTimeSection";
import TexarkanaServicesSection from "./components/ServicesSection";
import TexarkanaLocalAreaSection from "./components/LocalAreaSection";
import TexarkanaTravelTimingSection from "./components/TravelTimingSection";
import TexarkanaDestinationsSection from "./components/DestinationsSection";
import TexarkanaTestimonialsSection from "./components/TestimonialsSection";
import TexarkanaAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import TexarkanaComparisonSection from "./components/ComparisonSection";
import TexarkanaMistakesSection from "./components/MistakesSection";
import TexarkanaHowItWorksSection from "./components/HowItWorksSection";
import TexarkanaFAQSection, { faqs } from "./components/FAQSection";
import TexarkanaHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import TexarkanaFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function TexarkanaPage() {
  useEffect(() => {
    document.title = "Emergency Passport Texarkana, AR — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/texarkana/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Texarkana, AR — as fast as 24 hours. Serving railroad professionals, college students, and Ark-La-Tex mission teams. A+ BBB rated, registered State Dept. courier.");
    setMeta("geo.placename", "Texarkana, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "33.4418;-94.0477");
    setMeta("ICBM", "33.4418, -94.0477");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Arkansas", item: "https://fastpassportcenter.com/arkansas/" },
          { "@type": "ListItem", position: 3, name: "Texarkana Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Texarkana, AR and the Ark-La-Tex tri-state region. Serving railroad professionals, college students, and mission teams.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Texarkana", containedInPlace: { "@type": "State", name: "Arkansas" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Texarkana" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <TexarkanaHeroSection />
      <TexarkanaStickyNav />
      <TexarkanaProcessingTimeSection />
      <TexarkanaServicesSection />
      <TexarkanaLocalAreaSection />
      <TexarkanaTravelTimingSection />
      <TexarkanaDestinationsSection />
      <TexarkanaTestimonialsSection />
      <TexarkanaAcceptanceFacilitiesSection />
      <TexarkanaComparisonSection />
      <TexarkanaMistakesSection />
      <TexarkanaHowItWorksSection />
      <TexarkanaFAQSection />
      <TexarkanaHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/texarkana" state="AR" />
      <TexarkanaFinalCTA />
    </div>
  );
}